import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Row, Col, Select, Space, Typography } from 'antd';
import { useUser } from '../../../context/UserContext';
import { getPrices } from '../../../services/api';

const { Option } = Select;
const { Text } = Typography;

interface VehicleDetailsFormProps {
    onFinish: (values: any) => void;
    loading: boolean;
    initialValues: any;
    isEditing: boolean;
    onContinue: () => void;
}

export const VehicleDetailsForm: React.FC<VehicleDetailsFormProps> = ({ onFinish, loading, initialValues, isEditing, onContinue }) => {
    const [form] = Form.useForm();
    const { credits } = useUser();
    const [prices, setPrices] = useState({ quick_run_cost: 1, full_run_cost: 6 });

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const pricesData = await getPrices();
                if (pricesData.quick_run_cost && pricesData.full_run_cost) {
                    setPrices(pricesData);
                }
            } catch (error) {
                console.error("Failed to fetch prices", error);
            }
        };
        fetchPrices();
    }, []);

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
        }
    }, [initialValues, form]);

    const hasEnoughCredits = credits >= prices.quick_run_cost;

    return (
        <>
            {isEditing && (
                <div style={{ textAlign: 'right', marginBottom: 24 }}>
                    <Button type="primary" size="large" onClick={onContinue} style={{ minWidth: 200 }}>
                        Continue to Features
                    </Button>
                </div>
            )}
            <Card title="Vehicle Research Assistant">
            <div style={{ marginBottom: 24 }}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                    Welcome! Start by entering the details of the vehicle you're interested in.
                    We'll help you identify key features and generate a comprehensive market report tailored to your specific preferences.
                </Text>
            </div>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark="optional"
            >
                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            name="Year"
                            label="Year"
                            rules={[{ required: true, message: 'Please enter the desired year' }]}
                        >
                            <Input placeholder="e.g. 2020" disabled={loading} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            name="Make"
                            label="Make"
                            rules={[{ required: true, message: 'Please enter the desired make' }]}
                        >
                            <Input placeholder="e.g. Toyota" disabled={loading} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            name="Model"
                            label="Model"
                            rules={[{ required: true, message: 'Please enter the desired model' }]}
                        >
                            <Input placeholder="e.g. Camry" disabled={loading} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="Trims" label="Trims (Optional)">
                            <Select mode="tags" placeholder="e.g. XLE, SE" disabled={loading} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="Location"
                            label="Location"
                        >
                            <Input placeholder="e.g. Austin, TX" disabled={loading} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Form.Item name="Engine" label="Engine">
                            <Input placeholder="e.g. 2.5L 4-Cylinder" disabled={loading} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item name="Transmission" label="Transmission">
                            <Input placeholder="e.g. Automatic" disabled={loading} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item name="Drivetrain" label="Drivetrain">
                            <Select placeholder="Select Drivetrain" disabled={loading}>
                                <Option value="FWD">FWD</Option>
                                <Option value="RWD">RWD</Option>
                                <Option value="AWD">AWD</Option>
                                <Option value="4WD">4WD</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Space direction="vertical">
                        <Space>
                            <Button type="primary" htmlType="submit" size="large" loading={loading} style={{ minWidth: 200 }} disabled={loading || !hasEnoughCredits}>
                                {loading ? 'Gathering Features...' : (isEditing ? 'Search Again' : 'Search')}
                            </Button>
                        </Space>
                        <Text type="secondary">
                            Cost: {prices.quick_run_cost} credit{prices.quick_run_cost !== 1 ? 's' : ''}.
                            {!hasEnoughCredits && " You don't have enough credits."}
                        </Text>
                    </Space>
                </div>
            </Form>
        </Card>
    </>
    );
};
